import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
  EBundlesViewModel
} from '@appcharge/shared-ui';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import usePermissions from 'api/usePermissions';
import usePricing from 'api/usePricing';
import { useProduct } from 'api/useProduct';
import useSections from 'api/useSections';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import {
  EBadgeType,
  ETaskId,
  Offer,
  ProductsSequenceProduct,
  Section
} from 'common/contracts';
import {
  EAssetType,
  EFeatureFlag,
  EProductPriority,
  ESaleDiscountDisplayType,
  ESectionOfferType,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import { useFormik } from 'formik';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { convertProgressBarPointsToApiFormat } from 'utils/barPointsUtils';
import { transformQuantityValue } from 'utils/formattingUtil';
import { isPriceLocalized as isPriceLocalizedUtil } from 'utils/isPriceLocalized';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import {
  buildOfferBadges,
  buildBaseOfferPayload,
  buildBootInfo,
  buildFormTitle,
  buildProductsListForPreview,
  buildSectionTabPayload,
  checkUniqueness,
  clampPriceValue,
  createBadgeFilter,
  getInitialBadgeValues,
  getRelevantBadgeType,
  mapApiProductsToLocal
} from 'views/Offers/Offer.utils';
import {
  BUNDLE_MODEL_BADGE_MAP,
  DEFAULT_OFFER_DESIGN_EXTERNAL_ID,
  EMPTY_TAB,
  TRANSLATE_MOCK_MAP
} from 'views/Offers/Offer.consts';
import { IProductProps } from 'views/Offers/Offer.types';
import { useBadgeForm } from 'views/Offers/hooks/useBadgeForm';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormData } from 'views/Offers/hooks/useOfferFormData';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { useProgressBarForm } from 'views/Offers/hooks/useProgressBarForm';
import {
  descriptionFieldSchema,
  discountFieldSchema,
  nameFieldSchema,
  offerUiIdFieldSchema,
  publisherOfferIdFieldSchema,
  salePercentageFieldSchema,
  segmentsFieldSchema,
  standardPriceFieldSchema,
  tagsFieldSchema
} from 'views/Offers/Offer.validation';
import * as yup from 'yup';

import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { Product } from 'components/ProductsForm/ProductsForm.types';
import { generatePlayerData } from 'components/TemplatesStudio/Components/FormPage/Preview/utils/playerData';
import { TabsItem } from 'components/Theme/StoreTheme/storeTheme.types';
import { Separator } from 'components/ui/separator';

import { OfferPreviewSection } from 'views/Offers/components/OfferPreviewSection';
import { BadgesSection } from './components/BadgesSection';
import { GeneralSection } from './components/GeneralSection';
import { ProductsSection } from './components/ProductsSection';
import { SaleDiscountSection } from './components/SaleDiscountSection';
import { useOfferPreviewData } from 'views/Offers/hooks/useOfferPreviewData';

import {
  buildBundleFormInitialValues,
  buildCurrentBundle,
  buildOfferData
} from './BundlesForm.utils';

import 'style/forms.scss';

interface IBundlesFormProps {
  edit?: boolean;
  dup?: boolean;
}

export const BundlesForm: React.FC<IBundlesFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { canUserEdit } = usePermissions();
  const { featureFlags } = useFeatureFlags({ publisherId: publisherId });
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { addOffer, updateOffer, getOffer, getOffers, formatProductQuantity } =
    useOffers({ offerId });
  const { setTaskStatus, hasCategories, isWebStore } = useChecklistState();
  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Offer',
    addOffer,
    updateOffer,
    onSuccess: (): void => {
      if (!edit && hasCategories && isWebStore) {
        setTaskStatus(ETaskId.ACTIVATE_FIRST_BUNDLE);
      }
    }
  });
  const {
    offerDesignOptions,
    defaultOfferDesignId,
    getOfferUI,
    setCurrentOfferUI
  } = useOfferDesignOptions({
    publisherId: publisherId || '',
    offerDesignType: OfferDesignTypes.Bundle,
    defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.BUNDLE],
    offerId
  });
  const hasFeatureFlagTags = featureFlags?.[EFeatureFlag.DASHBOARD_TAGS];
  const { getGeneralTheme: generalTheme, getStoreTheme } =
    useTheme(publisherId);
  const { data, isLoading, chosenSegment, setChosenSegment } = useOfferFormData({
    offerId,
    publisherId,
    getOffer,
    onDataLoaded: (result: Partial<Offer>): void => {
      setCurrentOfferUI(
        result.offerUi?._id || result.offerUi?.offerUiId || ''
      );
      setProducts(
        mapApiProductsToLocal(result.productsSequence?.[0]?.products as any[] || [])
      );
      setProgressBarFormValues({
        progressBarPoints:
          result.productsSequence?.[0]?.progressBarPoints?.[0]?.points || null,
        progressBarId:
          result.productsSequence?.[0]?.progressBarPoints?.[0]?.barId || null
      });
    }
  });
  const { getSectionsByOfferType: getSections } = useSections(
    publisherId,
    ESectionOfferType.BUNDLE
  );
  const { getTabs } = useTabs(publisherId);
  const [dirtyProducts, setDirtyProducts] = useState(false);
  const {
    progressBarFormValues,
    setProgressBarFormValues,
    isProgressBarFormValid,
    dirtyProgressBar,
    handleProgressBarFormChange
  } = useProgressBarForm();
  const { getPricings } = usePricing();
  const [isPriceLocalized, setIsPriceLocalized] = useState(true);
  const priceTypingDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const viewModel = generalTheme.data?.bundlesViewModel as EBundlesViewModel;

  useEffect(() => {
    if (!getPricings.data && !getPricings.isFetching) {
      getPricings.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPricings.data, getPricings.isFetching]);

  const isUnique = useCallback(
    (value: string | undefined, field: keyof Offer): boolean =>
      checkUniqueness(value, field, getOffers.data?.offers, data, !!edit),
    [getOffers.data?.offers, data, edit]
  );

  const bundleModel = useMemo(
    () => generalTheme.data?.bundlesInternalViewModel,
    [generalTheme.data?.bundlesInternalViewModel]
  );

  const enableTabs: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.STORE_TABS],
    [featureFlags]
  );

  const offerSchema = useMemo(
    () =>
      yup.object().shape({
        name: nameFieldSchema(isUnique),
        description: descriptionFieldSchema(),
        offerUiId: offerUiIdFieldSchema(),
        publisherOfferId: publisherOfferIdFieldSchema(isUnique),
        price: standardPriceFieldSchema(),
        discount: discountFieldSchema(),
        salePercentage: salePercentageFieldSchema(),
        segments: segmentsFieldSchema(),
        tags: tagsFieldSchema()
      }),
    [isUnique]
  );

  const relevantBadgeType: EBadgeType | 'both' = useMemo(
    () => getRelevantBadgeType(bundleModel, BUNDLE_MODEL_BADGE_MAP, viewModel),
    [viewModel, bundleModel]
  );

  const [products, setProducts] = useState<IProductProps[]>([]);
  const [currentProduct, setCurrentProduct] =
    useState<ProductsSequenceProduct | null>(null);
  const { data: productsData, isLoading: isLoadingProducts } =
    useProduct(publisherId).getProducts;

  const productsList: Product[] = productsData?.result;

  const initialBadgeValues = getInitialBadgeValues(
    data?.badges,
    createBadgeFilter(relevantBadgeType, viewModel)
  );

  const {
    badges,
    emblemBadges,
    ribbonBadges,
    badgeList,
    filterRelevantBadges,
    setSelectedBadgeId,
    selectedPublisherBadgeId
  } = useBadgeForm({
    publisherId,
    relevantBadgeType,
    viewModel,
    initialSelectedBadgeId: initialBadgeValues.badgeId
  });

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    dirty,
    isValid,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField
  } = useFormik({
    validateOnMount: true,
    validationSchema: offerSchema,
    enableReinitialize: true,
    initialValues: {
      ...buildBundleFormInitialValues({
        data,
        edit,
        dup,
        defaultOfferDesignId,
        getOfferResult: getOffer?.data?.result
      }),
      badge: initialBadgeValues.badgeId,
      publisherBadgeId: initialBadgeValues.publisherBadgeId
    },
    onSubmit: async (values) => {
      const offerBadges = buildOfferBadges(
        values.badge,
        values.publisherBadgeId,
        selectedPublisherBadgeId
      );

      const progressBarPoints = convertProgressBarPointsToApiFormat(
        progressBarFormValues
      );

      const { sectionId, tabId } = buildSectionTabPayload(
        values.section,
        values.tab
      );

      const newOffer: Partial<Offer> & {
        sectionId?: string | null;
        tabId?: string | null;
      } = {
        ...buildBaseOfferPayload(values, values.segments, OfferType.BUNDLE),
        badges: offerBadges,
        productSale: {
          sale: values.salePercentage,
          type:
            values.salePercentageDisplayType ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        priceDiscount: {
          discount: values.discount,
          type:
            values.priceDiscountDisplayType ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        productsSequence: [
          {
            index: 1,
            products: products.map((p) => ({
              productId: p.productId,
              quantity: p.amount,
              ...(p.publisherProductId ? { publisherProductId: p.publisherProductId } : {}),
              priority: p.priority
            })),
            priceInUsdCents: new Decimal(values.price).mul(100).toNumber(),
            progressBarPoints
          }
        ],
        sectionId,
        tabId
      };

      submitOffer(newOffer, offerId);
    }
  });

  useEffect(() => {
    setSelectedBadgeId(values.badge);
  }, [values.badge]);

  const removeProduct = useCallback(
    (product: { _id: string; productId?: string }) => {
      const productId = product.productId || product._id;
      setDirtyProducts(true);
      setProducts((prevProducts) => {
        return prevProducts.filter((p) => p._id !== product._id);
      });
      setFieldValue(
        'productsSequence[0].products',
        (prevProducts: ProductsSequenceProduct[]) => {
          return prevProducts.filter((p) => p.productId !== productId);
        }
      );
    },
    [setFieldValue]
  );

  const editProduct = useCallback(
    (product: {
      _id: string;
      productId?: string;
      image?: string;
      amount?: number | string;
      publisherProductId?: string;
      priority?: string | number;
    }) => {
      setDirtyProducts(true);
      removeProduct(product);
      setCurrentProduct({
        product: product.image,
        productId: product._id,
        quantity: product.amount || 0,
        publisherProductId: product.publisherProductId,
        priority: String(product.priority || EProductPriority.SUB)
      });
    },
    [removeProduct]
  );

  const handleProductReorder = useCallback(
    (reordered: Array<{ _id: string; productId?: string }>): void => {
      setDirtyProducts(true);
      setProducts(reordered as typeof products);
      const raw = values.productsSequence?.[0]?.products ?? [];
      const currentFormikProducts = (
        Array.isArray(raw)
          ? raw
          : raw && typeof raw === 'object' && !Array.isArray(raw)
            ? Object.values(raw)
            : []
      ) as ProductsSequenceProduct[];
      const reorderedFormikProducts = reordered
        .map((p) =>
          currentFormikProducts.find(
            (pr: ProductsSequenceProduct) => pr.productId === p.productId
          )
        )
        .filter((x): x is ProductsSequenceProduct => x != null);
      setFieldValue('productsSequence[0].products', reorderedFormikProducts);
    },
    [setDirtyProducts, setFieldValue, values.productsSequence]
  );

  const addProduct = useCallback(async (): Promise<void> => {
    const foundProduct = productsData!.result.find(
      (product: { productId: string | undefined }) =>
        product.productId === currentProduct?.productId
    );

    const formattedProductQuantity = await formatProductQuantity.mutateAsync({
      quantity: currentProduct!.quantity?.toString() || '',
      productType: foundProduct.type
    });

    const newProduct: IProductProps = {
      _id: currentProduct!.productId!,
      productId: currentProduct!.productId!,
      image: foundProduct!.images[0].url,
      imagePrefix: foundProduct.images?.find((i: { type: EAssetType }) => {
        return i.type === EAssetType.PRODUCT_PREFIX;
      })?.url || '',
      name: foundProduct!.name,
      amount: currentProduct!.quantity || 0,
      type: foundProduct!.type,
      textFontColorHex: foundProduct!.textFontColorHex,
      prefix: foundProduct!.prefix,
      suffix: foundProduct!.suffix,
      quantityDisplay: formattedProductQuantity.data,
      priority: currentProduct?.priority || EProductPriority.SUB,
      publisherProductId: currentProduct?.publisherProductId
    };

    setProducts((prevProducts) => [...prevProducts, newProduct]);

    setFieldValue(
      'productsSequence[0].products',
      (prevProducts: ProductsSequenceProduct[]) => {
        const newProductSequence: Record<string, unknown> = {
          productId: newProduct.productId,
          quantity: newProduct.amount,
          priority: newProduct.priority
        };
        if (newProduct.publisherProductId) {
          newProductSequence.publisherProductId = newProduct.publisherProductId;
        }
        return [...prevProducts, newProductSequence];
      }
    );

    setCurrentProduct({
      productId: '',
      quantity: 0,
      publisherProductId: '',
      priority: EProductPriority.SUB
    });
  }, [
    productsData,
    currentProduct,
    formatProductQuantity,
    setFieldValue,
    setCurrentProduct
  ]);

  const isNameExists = useMemo(
    (): boolean =>
      Boolean(
        getOffers.data?.offers?.find((o: Offer) => o.name === values.name) && !edit
      ),
    [getOffers.data?.offers, values.name, edit]
  );

  const handleQuantityChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const newQuantity =
        e.target.value.length !== 0
          ? transformQuantityValue(e.target.value)
          : e.target.value;
      setCurrentProduct({
        productId: currentProduct?.productId || '',
        quantity: newQuantity,
        publisherProductId: currentProduct?.publisherProductId,
        priority: currentProduct?.priority || EProductPriority.SUB
      });
    },
    [currentProduct]
  );

  const handlePriorityChange = useCallback(
    (value: string): void => {
      setCurrentProduct({
        productId: currentProduct?.productId || '',
        quantity: currentProduct?.quantity || 0,
        publisherProductId: currentProduct?.publisherProductId,
        priority: value
      });
    },
    [currentProduct]
  );

  const transformPriceValue = useCallback(
    (): number => clampPriceValue(values.price),
    [values.price]
  );

  const sectionItems = useMemo(() => {
    const items = [{ value: '0', label: 'None' }];
    if (getSections?.data?.result?.length > 0) {
      const parsedSectionsData = getSections.data.result.map(
        (section: Section) => ({
          value: section.sectionId,
          label: section.name
        })
      );
      return items.concat(parsedSectionsData);
    }
    return items;
  }, [getSections?.data?.result]);

  const tabItems = useMemo((): Array<{ content: string; key: string; value: string }> => {
    if (!enableTabs) {
      return [];
    }

    const base = [EMPTY_TAB];
    if (getTabs.data?.length > 0) {
      const parsedTabsData = getTabs.data.map((tab: TabsItem) => ({
        content: tab.name,
        key: tab.tabId,
        value: tab.tabId
      }));
      return base.concat(parsedTabsData);
    }

    return base;
  }, [enableTabs, getTabs.data]);
  const productsListForPreview = useMemo(
    () => buildProductsListForPreview(products),
    [products]
  );

  const getProductsOptions = useMemo(() => {
    if (isLoadingProducts || !productsList) {
      return [];
    }
    const productIds = new Set(products.map((item) => item.productId));
    const filteredProducts = productsList.filter(
      (product: Product) => !productIds.has(product.productId as string)
    );
    if (filteredProducts.length === 0) {
      return [];
    }
    return filteredProducts.map((product) => ({
      url: product.images?.[0]?.url,
      name: product.name,
      key: product.name,
      value: product.productId
    }));
  }, [productsList, products, isLoadingProducts]);

  const selectedProduct =
    getProductsOptions.find(
      (product) => product.value === currentProduct?.productId
    ) || null;

  const priceLocalizedCheckDebounce = useCallback(
    (price: number): void => {
      if (priceTypingDebounceTimeout.current) {
        clearTimeout(priceTypingDebounceTimeout.current);
      }
      priceTypingDebounceTimeout.current = setTimeout(() => {
        setIsPriceLocalized(isPriceLocalizedUtil(price, getPricings.data?.result));
      }, 1000);
    },
    [getPricings.data?.result]
  );

  useEffect(() => {
    if (getPricings.data && values.price) {
      priceLocalizedCheckDebounce(
        new Decimal(values.price).times(100).toNumber()
      );
    }
  }, [getPricings.data, values.price, priceLocalizedCheckDebounce]);

  const bootInfo = useMemo(
    () => buildBootInfo(getStoreTheme.data, featureFlags),
    [getStoreTheme.data, featureFlags]
  );

  const dynamicOfferData = useOfferPreviewData({
    values,
    getOfferUI,
    productsListForPreview,
    badges,
    emblemBadges,
    ribbonBadges,
    transformPriceValue,
    buildCurrentOffer: buildCurrentBundle,
    buildOfferData
  });

  const previewProps = useMemo(
    () => ({
      translate: (key: string): string => TRANSLATE_MOCK_MAP[key] || key,
      playerData: generatePlayerData(),
      boot: bootInfo,
      offers: dynamicOfferData
    }),
    [bootInfo, dynamicOfferData]
  );

  const formTitle = useMemo(
    (): string => buildFormTitle(edit, 'Bundle Offer', getOffer.data?.result?.name),
    [edit, getOffer.data?.result?.name]
  );

  const isFormValid = useMemo(
    () =>
      !isLoading &&
      isValid &&
      isProgressBarFormValid &&
      canUserEdit &&
      (!edit || dirty || dirtyProducts || dirtyProgressBar),
    [
      isLoading,
      isValid,
      isProgressBarFormValid,
      canUserEdit,
      edit,
      dirty,
      dirtyProducts,
      dirtyProgressBar
    ]
  );

  const handleSave = useCallback((): void => {
    submitForm();
  }, [submitForm]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={handleSave}
      onClose={handleClose}
      previewContent={
        <OfferPreviewSection
          previewProps={previewProps}
          storeThemeData={getStoreTheme.data}
        />
      }
    >
      <div className="flex flex-col h-full pt-10 pb-10 w-[528px] min-h-fit">
            <GeneralSection
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              setTouched={setTouched}
              validateField={validateField}
              dup={dup}
              isNameExists={isNameExists}
              isPriceLocalized={isPriceLocalized}
              offerDesignOptions={offerDesignOptions}
              setCurrentOfferUI={setCurrentOfferUI}
              sectionItems={sectionItems}
              tabItems={tabItems}
              enableTabs={!!enableTabs}
              chosenSegment={chosenSegment}
              setChosenSegment={setChosenSegment}
            />
            <BadgesSection
              values={values}
              setFieldValue={setFieldValue}
              relevantBadgeType={relevantBadgeType}
              badgeList={badgeList}
            />
            <Separator className="my-9 bg-base-border" />
            <SaleDiscountSection
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
            />
            <Separator className="my-9 bg-base-border" />
            <ProductsSection
              values={values}
              setFieldValue={setFieldValue}
              currentProduct={currentProduct}
              getProductsOptions={getProductsOptions}
              selectedProduct={selectedProduct}
              setCurrentProduct={setCurrentProduct}
              addProduct={addProduct}
              handleQuantityChange={handleQuantityChange}
              handlePriorityChange={handlePriorityChange}
              products={products}
              editProduct={editProduct}
              removeProduct={removeProduct}
              handleProductReorder={handleProductReorder}
              hasFeatureFlagTags={!!hasFeatureFlagTags}
              progressBarFormValues={progressBarFormValues}
              handleProgressBarFormChange={handleProgressBarFormChange}
            />
          </div>
    </OfferFormLayout>
  );
};
